import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import {
  Sparkles, Star, Layers, Command, ChevronLeft, ChevronRight, Check,
} from 'lucide-react';

/**
 * Mini-tutorial de bienvenida (3 pasos) que se muestra en el primer login
 * de cada usuario. Persiste en localStorage por email.
 *
 * Permite re-abrirse manualmente desde el dropdown del usuario via
 * `window.dispatchEvent(new CustomEvent('chirino-show-onboarding'))`.
 *
 * Storage key: `chirino-onboarding-seen:<email>` = "1" cuando se completa.
 */
const STORAGE_PREFIX = 'chirino-onboarding-seen:';

const STEPS = [
  {
    icon: Star,
    accentColor: 'text-amber-500',
    bgColor: 'bg-amber-50 dark:bg-amber-950/30',
    title: '⭐ Favoritos personales',
    description: 'Marca tus pantallas más usadas con una estrella y aparecerán siempre al tope del sidebar.',
    bullets: [
      'Pasa el mouse sobre cualquier ítem del menú lateral.',
      'Aparece una estrella ☆ en el lado derecho.',
      'Haz clic — el ítem se sube al tope en la sección "⭐ Mis favoritos".',
      'Cada usuario tiene sus propios favoritos.',
    ],
  },
  {
    icon: Layers,
    accentColor: 'text-blue-500',
    bgColor: 'bg-blue-50 dark:bg-blue-950/30',
    title: '📂 Sub-grupos dentro de los módulos',
    description: 'Los módulos grandes (Contabilidad, Bodega, RRHH, Compras, Configuración) ahora muestran mini-encabezados que agrupan los ítems por temática.',
    bullets: [
      'Contabilidad → Pagos a Proveedores, Cobros a Clientes, DTE y SII, Tesorería.',
      'Bodega → Operación Diaria, Stock, Herramientas, Maestros.',
      'RRHH → Personal, Asistencia, Aprobaciones, Casino.',
      'Más fácil encontrar lo que buscas sin scrollear.',
    ],
  },
  {
    icon: Command,
    accentColor: 'text-violet-500',
    bgColor: 'bg-violet-50 dark:bg-violet-950/30',
    title: '🔍 Búsqueda rápida (Cmd+K)',
    description: 'Presiona Cmd+K (Mac) o Ctrl+K (Windows) en cualquier momento para abrir el buscador global.',
    bullets: [
      'Escribe el nombre de la pantalla que buscas (ej. "asistencia", "ocs", "factoring").',
      'Te lleva directo sin pasar por el sidebar.',
      'También busca clientes, proveedores, OCs y trabajadores.',
      'Atajo de productividad nº1 — pruébalo ahora mismo después de cerrar este tutorial.',
    ],
  },
];

export default function OnboardingTour() {
  const { user } = useAuth();
  const userKey = user?.email || 'guest';
  const storageKey = `${STORAGE_PREFIX}${userKey}`;

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  // Auto-open on first login (delay 1s para que cargue el sidebar)
  useEffect(() => {
    if (!user?.email) return;
    const seen = (() => {
      try { return localStorage.getItem(storageKey) === '1'; } catch { return false; }
    })();
    if (seen) return;

    const t = setTimeout(() => {
      setStep(0);
      setOpen(true);
    }, 1200);
    return () => clearTimeout(t);
  }, [user?.email, storageKey]);

  // Permitir re-abrir el tour vía custom event (lo dispara el menú "Ayuda" → "Volver a ver tutorial")
  useEffect(() => {
    const handler = () => { setStep(0); setOpen(true); };
    window.addEventListener('chirino-show-onboarding', handler);
    return () => window.removeEventListener('chirino-show-onboarding', handler);
  }, []);

  const close = useCallback((markSeen = true) => {
    if (markSeen) {
      try { localStorage.setItem(storageKey, '1'); } catch { /* ignore */ }
    }
    setOpen(false);
  }, [storageKey]);

  if (!user?.email) return null;

  const total = STEPS.length;
  const isLast = step === total - 1;
  const current = STEPS[step];
  const Icon = current.icon;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close(true)}>
      <DialogContent className="max-w-lg" data-testid="onboarding-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Bienvenido, {user?.nombre?.split(' ')[0] || user.email.split('@')[0]} 👋
          </DialogTitle>
        </DialogHeader>

        {/* Progress dots */}
        <div className="flex justify-center gap-1.5 py-2" data-testid="onboarding-progress">
          {STEPS.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setStep(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? 'w-8 bg-primary' : 'w-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/50'
              }`}
              aria-label={`Ir al paso ${i + 1}`}
              data-testid={`onboarding-dot-${i}`}
            />
          ))}
        </div>

        {/* Step content */}
        <div className={`rounded-xl ${current.bgColor} p-5 space-y-3`} data-testid={`onboarding-step-${step}`}>
          <div className="flex items-center gap-3">
            <div className={`rounded-lg bg-white dark:bg-slate-900 p-2.5 shadow-sm ${current.accentColor}`}>
              <Icon className="w-6 h-6" strokeWidth={1.7} />
            </div>
            <div>
              <h3 className="text-base font-semibold leading-tight">{current.title}</h3>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">{current.description}</p>
          <ul className="space-y-1.5 pl-1">
            {current.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-xs">
                <Check className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${current.accentColor}`} strokeWidth={2.5} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Footer / Nav */}
        <div className="flex items-center justify-between gap-2 pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => close(true)}
            className="text-muted-foreground"
            data-testid="onboarding-skip"
          >
            Saltar tutorial
          </Button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button variant="outline" size="sm" onClick={() => setStep(step - 1)} data-testid="onboarding-prev">
                <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
              </Button>
            )}
            {!isLast ? (
              <Button size="sm" onClick={() => setStep(step + 1)} data-testid="onboarding-next">
                Siguiente <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button size="sm" onClick={() => close(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="onboarding-finish">
                <Check className="w-4 h-4 mr-1" /> ¡Listo!
              </Button>
            )}
          </div>
        </div>

        <p className="text-[10px] text-muted-foreground text-center pt-1">
          Puedes volver a ver este tutorial desde tu menú de usuario → "Ver tutorial".
        </p>
      </DialogContent>
    </Dialog>
  );
}
